/**
 * EstadoDaSecao — carregando / erro / vazio de uma seção do Início (PR 5).
 *
 * Uma peça só para as três seções (comunicados, notificações) falarem a mesma
 * língua. O estado VAZIO é o caso comum no lançamento: quase todo parceiro
 * verá 0 notificações no primeiro dia (as 100 de teste somem no deploy) e a
 * Central tem 2 posts. Por isso o vazio é calmo e intencional — ícone suave,
 * uma frase, sem moldura vermelha nem "erro" implícito.
 *
 * - carregando: esqueleto de 3 linhas + texto só para leitor de tela (`role="status"`).
 * - erro: caixa com "Tentar de novo" (só aparece se houver `aoTentarDeNovo`).
 * - vazio: ícone + `mensagem` (+ `detalhe` opcional); `compacto` encolhe para
 *   caber no painel de notificações do celular, que fica ACIMA dos comunicados.
 * - indisponivel (I4, revisão final): um item específico que não existe mais
 *   (post expirado/fora da Audiência) — INFORMATIVO, não erro: âmbar, sem
 *   ícone de alarme, sem "Tentar de novo" (não há o que tentar de novo — o
 *   item não volta). Mesma cor que `AvisoLeiturasPendentes` usa para
 *   pendência (âmbar = "algo a notar", nunca vermelho = "algo quebrou").
 */
import { AlertCircle, Inbox, Info, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type TipoDoEstado = "carregando" | "erro" | "vazio" | "indisponivel";

export interface EstadoDaSecaoProps {
  tipo: TipoDoEstado;
  mensagem?: string;
  /** Segunda linha do vazio, em tom menor ("Quando algo novo chegar…"). */
  detalhe?: string;
  aoTentarDeNovo?: () => void;
  /** Ícone do vazio; padrão é uma caixa de entrada. */
  icone?: ReactNode;
  /** Vazio/carregando mais baixos, para o painel compacto do celular. */
  compacto?: boolean;
}

const MENSAGEM_PADRAO: Record<TipoDoEstado, string> = {
  carregando: "Carregando…",
  erro: "Não foi possível carregar.",
  vazio: "Nada por aqui por enquanto.",
  indisponivel: "Este conteúdo não está mais disponível.",
};

export function EstadoDaSecao({
  tipo,
  mensagem,
  detalhe,
  aoTentarDeNovo,
  icone,
  compacto = false,
}: EstadoDaSecaoProps) {
  const texto = mensagem ?? MENSAGEM_PADRAO[tipo];
  const testId = `estado-secao-${tipo}`;

  if (tipo === "carregando") {
    const linhas = compacto ? [0, 1] : [0, 1, 2];
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-testid={testId}
        className={cn("space-y-3", compacto ? "py-2" : "py-1")}
      >
        <span className="sr-only">{texto}</span>
        {linhas.map((i) => (
          <div key={i} className="flex items-start gap-3" aria-hidden="true">
            <Skeleton className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-100" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3 bg-emerald-100/80" />
              <Skeleton className="h-3 w-full bg-emerald-50" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tipo === "erro") {
    return (
      <div
        role="alert"
        data-testid={testId}
        // Texto em cima, botão embaixo — sempre. Lado a lado ficava bom na
        // largura dos comunicados, mas na coluna de 340 px espremia a frase em
        // quatro linhas ao lado do botão; e não há container query sem plugin.
        className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50/70 p-4"
      >
        <p className="flex items-start gap-2 text-sm text-red-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" aria-hidden="true" />
          <span>{texto}</span>
        </p>
        {aoTentarDeNovo && (
          <Button
            type="button"
            variant="outline"
            onClick={aoTentarDeNovo}
            className="h-12 w-full gap-2 border-red-200 bg-white px-4 text-sm text-red-800 hover:bg-red-100 hover:text-red-900 sm:h-10 sm:w-auto sm:self-start"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Tentar de novo
          </Button>
        )}
      </div>
    );
  }

  if (tipo === "indisponivel") {
    return (
      <div
        role="status"
        data-testid={testId}
        className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-4"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
        <p className="text-sm text-amber-900">{texto}</p>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={cn(
        "flex flex-col items-center text-center",
        compacto ? "gap-2 px-4 py-5" : "gap-3 px-4 py-10"
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex items-center justify-center rounded-full bg-emerald-50 text-emerald-400 [&>svg]:h-1/2 [&>svg]:w-1/2",
          compacto ? "h-10 w-10" : "h-14 w-14"
        )}
      >
        {icone ?? <Inbox />}
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-700">{texto}</p>
        {detalhe && <p className="text-xs text-slate-500">{detalhe}</p>}
      </div>
    </div>
  );
}

export default EstadoDaSecao;
